import { getFullnodeUrl, SuiClient } from '@mysten/sui/client';
import { SuiGraphQLClient } from '@mysten/sui/graphql';
import { graphql } from '@mysten/sui/graphql/schemas/latest';
import { conf, CONF_ENV } from '../common/constants.js';
import { poolDetailsMap } from '../common/maps.js';
import { parsers } from '../utils/parser.js';
export class Blockchain {
    network;
    gqlClient;
    suiClient;
    constructor(network) {
        this.network = network;
        this.suiClient = new SuiClient({
            url: getFullnodeUrl(network),
        });
        this.gqlClient = new SuiGraphQLClient({
            url: network === 'testnet'
                ? 'https://graphql.testnet.sui.io/graphql'
                : 'https://graphql.mainnet.sui.io/graphql',
        });
    }
    /**
     * Get estimated gas budget for a transaction
     */
    async getEstimatedGasBudget(tx, sender) {
        try {
            const simResult = await this.suiClient.devInspectTransactionBlock({
                transactionBlock: tx,
                sender,
            });
            return (Number(simResult.effects.gasUsed.computationCost) +
                Number(simResult.effects.gasUsed.nonRefundableStorageFee) +
                1e8);
        }
        catch (err) {
            console.error(`Error estimating transaction gasBudget`, err);
            return undefined;
        }
    }
    async getObject(objectId) {
        const query = graphql(`
      query getObject($objectId: SuiAddress!) {
        object(address: $objectId) {
          asMoveObject {
            contents {
              json
            }
          }
        }
      }
    `);
        const result = await this.gqlClient.query({
            query,
            variables: { objectId },
        });
        return result.data?.object?.asMoveObject?.contents?.json;
    }
    async multiGetObjects(objectIds) {
        const query = graphql(`
      query multiGetObjects($objectIds: [ObjectKey!]!) {
        multiGetObjects(keys: $objectIds) {
          asMoveObject {
            contents {
              json
            }
          }
        }
      }
    `);
        const result = await this.gqlClient.query({
            query,
            variables: { objectIds: objectIds.map((id) => ({ address: id })) },
        });
        return result.data?.multiGetObjects?.map((obj) => obj?.asMoveObject?.contents?.json);
    }
    async getReceipt(address, type) {
        const query = graphql(`
      query getReceipt($address: SuiAddress!, $type: String!) {
        objects(filter: { owner: $address, type: $type }) {
          nodes {
            asMoveObject {
              contents {
                json
              }
            }
          }
        }
      }
    `);
        const result = await this.gqlClient.query({
            query,
            variables: { address, type },
        });
        return result.data?.objects?.nodes.map((obj) => obj?.asMoveObject?.contents?.json);
    }
    async multiGetReceipts(address, types) {
        const batches = [];
        for (let i = 0; i < types.length; i += 10) {
            batches.push(types.slice(i, i + 10));
        }
        const promises = [];
        for (const batch of batches) {
            promises.push(this.gqlClient.query({
                query: this.getMultiReceiptsQuery(batch),
                variables: { address },
            }));
        }
        const results = await Promise.all(promises);
        const receiptsMap = new Map();
        for (const result of results) {
            for (const receipts of Object.values(result.data)) {
                if (receipts.nodes.length > 0) {
                    receiptsMap.set(receipts.nodes[0].asMoveObject.contents.type.repr, receipts.nodes.map((obj) => obj?.asMoveObject?.contents?.json));
                }
            }
        }
        return receiptsMap;
    }
    getMultiReceiptsQuery(types) {
        let char = 65;
        let query = `query multiGetReceipts($address: SuiAddress!) {`;
        types.forEach((type) => {
            query += `
        ${String.fromCharCode(char)}: objects(
          filter: {owner: $address, type: "${type}"}
        ) {
          nodes {
            asMoveObject {
              contents {
                type {
                  repr
                }
                json
              }
            }
          }
        }
      `;
            char++;
        });
        query += `}`;
        return graphql(query);
    }
    async getPool(poolId) {
        const poolObjectId = poolId;
        const pool = await this.suiClient.getObject({
            id: poolObjectId,
            options: {
                showContent: true,
            },
        });
        if (pool.data) {
            switch (poolDetailsMap[poolId].strategyType) {
                case 'ALPHA-VAULT':
                    return parsers.parseAlphaPool(pool.data);
                case 'FUNGIBLE-DOUBLE-ASSET-POOL':
                    return parsers.parseFungiblePool(pool.data);
                default:
                    return parsers.parsePool(pool.data);
            }
        }
        throw new Error(`Pool for poolId - ${poolId} not found`);
    }
    async getAlphaPosition(positionId) {
        const position = await this.suiClient.getObject({
            id: positionId,
            options: {
                showContent: true,
            },
        });
        return parsers.parseAlphaPosition(position.data);
    }
    async getAlphaFiReceipt(address) {
        const receipts = await this.suiClient.getOwnedObjects({
            owner: address,
            filter: {
                StructType: conf[CONF_ENV].ALPHAFI_RECEIPT_TYPE,
            },
            options: {
                showContent: true,
            },
        });
        return receipts.data.map((receipt) => parsers.parseAlphaFiReceipt(receipt.data));
    }
    async getReceiptOld(poolId, address) {
        const res = [];
        let currentCursor = null;
        while (true) {
            const paginatedObjects = await this.suiClient.getOwnedObjects({
                owner: address,
                cursor: currentCursor,
                filter: {
                    StructType: poolDetailsMap[poolId].receipt.type,
                },
                options: {
                    showContent: true,
                },
            });
            // Traverse the current page data and push to coins array
            paginatedObjects.data.forEach((obj) => {
                if (obj.data)
                    res.push(obj.data);
            });
            // Check if there's a next page
            if (paginatedObjects.hasNextPage && paginatedObjects.nextCursor) {
                currentCursor = paginatedObjects.nextCursor;
            }
            else {
                // No more pages available
                break;
            }
        }
        const receipts = [];
        res.forEach((receipt) => {
            if (receipt.content.fields.name !== poolDetailsMap[poolId].receipt.name) {
                return;
            }
            switch (poolDetailsMap[poolId].strategyType) {
                case 'ALPHA-VAULT':
                    receipts.push(parsers.parseAlphaReceipt(receipt));
                default:
                    receipts.push(parsers.parseReceipt(receipt));
            }
        });
        return receipts.length > 0 ? receipts[0] : null;
    }
}
//# sourceMappingURL=blockchain.js.map